import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useFilters } from '@/stores/filters/filters';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import { familyNodeId, groupId } from '@/components/jobArchitecture/helpers';

export const JOB_ARCHITECTURE_GROUP_FILTER_ID = 'job_architecture_group';
export const JOB_ARCHITECTURE_FAMILY_FILTER_ID = 'job_architecture_family';
export const JOB_ARCHITECTURE_PROFILE_FILTER_ID = 'job_architecture_profile';
export const JOB_ARCHITECTURE_JOB_LEVEL_FILTER_ID = 'job_architecture_job_level';

export const JOB_ARCHITECTURE_FILTER_QUERY_KEYS = [
  JOB_ARCHITECTURE_GROUP_FILTER_ID,
  JOB_ARCHITECTURE_FAMILY_FILTER_ID,
  JOB_ARCHITECTURE_PROFILE_FILTER_ID,
  JOB_ARCHITECTURE_JOB_LEVEL_FILTER_ID,
];

export const mergeJobArchitectureRouteQuery = (routeQuery, mergedTempFilters) => {
  const next = { ...routeQuery };

  JOB_ARCHITECTURE_FILTER_QUERY_KEYS.forEach((key) => {
    delete next[key];
  });

  return { ...next, ...mergedTempFilters };
};

const architectureSearchText = ref('');

export const applySearchFilter = (tree, searchText) => {
  const q = (searchText || '').trim();

  if (!q || !tree?.groups) {
    return tree;
  }

  const searchTerm = q.toLowerCase();
  const newGroups = [];

  tree.groups.forEach((group) => {
    const families = group.families || [];
    const groupName = group.name || '';

    if (groupName.toLowerCase().includes(searchTerm)) {
      newGroups.push({
        ...group,
        families: families.map((family) => ({
          ...family,
          profiles: family.profiles || [],
        })),
      });

      return;
    }

    const newFamilies = [];

    families.forEach((family) => {
      const profiles = family.profiles || [];
      const familyName = family.name || '';

      if (familyName.toLowerCase().includes(searchTerm)) {
        newFamilies.push({
          ...family,
          profiles,
        });

        return;
      }

      const filteredProfiles = profiles.filter((profile) => (
        (profile?.title || '').toLowerCase().includes(searchTerm)
      ));

      if (filteredProfiles.length > 0) {
        newFamilies.push({ ...family, profiles: filteredProfiles });
      }
    });

    if (newFamilies.length > 0) {
      newGroups.push({ ...group, families: newFamilies });
    }
  });

  return { ...tree, groups: newGroups };
};

// Build filter options from architecture tree for client-side filtering.
export const deriveFilterFields = (tree) => {
  const seenGroups = new Set();
  const groupOptions = [];

  const seenFamilies = new Set();
  const familyOptions = [];

  const seenProfiles = new Set();
  const profileOptions = [];

  const seenJobLevels = new Set();
  const jobLevelOptions = [];

  (tree?.groups || []).forEach((group) => {
    const gid = groupId(group);

    if (!seenGroups.has(gid)) {
      seenGroups.add(gid);
      groupOptions.push({ id: gid, label: group.name || 'Uncategorized' });
    }

    (group.families || []).forEach((family) => {
      const fid = familyNodeId(group, family);

      if (!seenFamilies.has(fid)) {
        seenFamilies.add(fid);
        familyOptions.push({ id: fid, label: family.name || 'Uncategorized' });
      }

      (family.profiles || []).forEach((profile) => {
        const { job_level: jobLevel, ulid, title } = profile;

        if (ulid && title && !seenProfiles.has(ulid)) {
          seenProfiles.add(ulid);
          profileOptions.push({ id: ulid, label: title });
        }

        if (jobLevel && !seenJobLevels.has(jobLevel)) {
          seenJobLevels.add(jobLevel);
          jobLevelOptions.push({ id: jobLevel, label: jobLevel });
        }
      });
    });
  });

  groupOptions.sort((a, b) => a.label.localeCompare(b.label));
  familyOptions.sort((a, b) => a.label.localeCompare(b.label));
  profileOptions.sort((a, b) => a.label.localeCompare(b.label));
  jobLevelOptions.sort((a, b) => a.label.localeCompare(b.label));

  return [
    {
      id: JOB_ARCHITECTURE_GROUP_FILTER_ID,
      label: 'Job Family Group',
      options: groupOptions,
      value: [],
      alwaysOn: true,
      category: 'basic',
    },
    {
      id: JOB_ARCHITECTURE_FAMILY_FILTER_ID,
      label: 'Job Family',
      options: familyOptions,
      value: [],
      alwaysOn: true,
      category: 'basic',
    },
    {
      id: JOB_ARCHITECTURE_PROFILE_FILTER_ID,
      label: 'Job Profile',
      options: profileOptions,
      value: [],
      alwaysOn: true,
      category: 'basic',
    },
    {
      id: JOB_ARCHITECTURE_JOB_LEVEL_FILTER_ID,
      label: 'Job Level',
      options: jobLevelOptions,
      value: [],
      alwaysOn: true,
      category: 'basic',
    },
  ];
};

// Prune tree by selected groups / families / profiles / job levels, removing empty nodes.
export const applyClientFilters = (
  tree,
  selectedGroups,
  selectedFamilies,
  selectedProfiles,
  selectedJobLevels,
) => {
  if (!tree?.groups) {
    return tree;
  }

  const hasGroupFilter = selectedGroups.length > 0;
  const hasFamilyFilter = selectedFamilies.length > 0;
  const hasProfileFilter = selectedProfiles.length > 0;
  const hasLevelFilter = selectedJobLevels.length > 0;

  if (
    !hasGroupFilter
    && !hasFamilyFilter
    && !hasProfileFilter
    && !hasLevelFilter
  ) {
    return tree;
  }

  const groupSet = new Set(selectedGroups);
  const familySet = new Set(selectedFamilies);
  const profileSet = new Set(selectedProfiles);
  const levelSet = new Set(selectedJobLevels);

  const newGroups = [];

  tree.groups.forEach((group) => {
    if (hasGroupFilter && !groupSet.has(groupId(group))) {
      return;
    }

    const newFamilies = [];

    (group.families || []).forEach((family) => {
      if (hasFamilyFilter && !familySet.has(familyNodeId(group, family))) {
        return;
      }

      const filteredProfiles = (family.profiles || []).filter((profile) => {
        if (hasProfileFilter && !profileSet.has(profile.ulid)) return false;
        if (hasLevelFilter && !levelSet.has(profile.job_level)) return false;

        return true;
      });

      if (filteredProfiles.length > 0) {
        newFamilies.push({ ...family, profiles: filteredProfiles });
      }
    });

    if (newFamilies.length > 0) {
      newGroups.push({ ...group, families: newFamilies });
    }
  });

  return { ...tree, groups: newGroups };
};

// Client-side Job Architecture filtering composable using the shared `useFilters` store
export const useJobArchitectureFilters = () => {
  const { fields, tempFilters } = storeToRefs(useFilters());
  const { treeData } = storeToRefs(useJobArchitecture());

  const { updateFilters, setFilterFields } = useFilters();

  const filteredTreeData = computed(() => {
    const tree = treeData.value;

    if (!tree) {
      return null;
    }

    const groupFilter = fields.value.find((field) => field.id === JOB_ARCHITECTURE_GROUP_FILTER_ID);
    const familyFilter = fields.value.find((field) => field.id === JOB_ARCHITECTURE_FAMILY_FILTER_ID);
    const profileFilter = fields.value.find((field) => field.id === JOB_ARCHITECTURE_PROFILE_FILTER_ID);
    const levelFilter = fields.value.find((field) => field.id === JOB_ARCHITECTURE_JOB_LEVEL_FILTER_ID);

    const groupIds = groupFilter?.value?.map((value) => value.id) || [];
    const familyIds = familyFilter?.value?.map((value) => value.id) || [];
    const profileIds = profileFilter?.value?.map((value) => value.id) || [];
    const levelIds = levelFilter?.value?.map((value) => value.id) || [];

    const afterFilters = applyClientFilters(tree, groupIds, familyIds, profileIds, levelIds);

    return applySearchFilter(afterFilters, architectureSearchText.value);
  });

  // Update filter options from the filtered data
  const refreshFilterOptions = () => {
    const filtered = filteredTreeData.value;

    if (!filtered) {
      return;
    }

    const newFields = deriveFilterFields(filtered);

    fields.value.forEach((appliedField) => {
      const matching = newFields.find((field) => field.id === appliedField.id);

      if (!matching) {
        return;
      }

      const optionIds = new Set(matching.options.map((option) => option.id));

      matching.value = appliedField.value.filter((value) => optionIds.has(value.id));
    });

    updateFilters({ filters: newFields });
  };

  // Populate filter fields from full `treeData` and optionally restore selections from URL
  const initializeFilters = (routeQuery = {}) => {
    const tree = treeData.value;

    if (!tree) {
      return;
    }

    const filterFields = deriveFilterFields(tree).map((field) => {
      const queryValue = routeQuery[field.id];

      if (queryValue == null || queryValue === '') {
        return field;
      }

      const ids = Array.isArray(queryValue) ? queryValue : [queryValue];
      const value = field.options.filter((option) => ids.includes(option.id));

      return { ...field, value };
    });

    updateFilters({ filters: filterFields });
    refreshFilterOptions();
  };

  // Copy `tempFilters` into applied `fields` via `setFilterFields`, then narrow options like a filter API response.
  const syncAppliedState = () => {
    setFilterFields(tempFilters.value.map((field) => ({ ...field })));

    refreshFilterOptions();
  };

  const setSearchText = (value) => {
    architectureSearchText.value = value || '';
  };

  const clearSearch = () => {
    architectureSearchText.value = '';
  };

  return {
    initializeFilters,
    syncAppliedState,
    refreshFilterOptions,
    filteredTreeData,
    searchText: architectureSearchText,
    setSearchText,
    clearSearch,
  };
};
